/**
 * RegisterMcpClientDialog — 3 tabs: OAuth DCR (recommended), Static
 * client (manual form), and Installer script (stretch).
 *
 * The "Static client" form posts to /admin/mcp/clients and shows the
 * one-time client_secret. Today the backend isn't shipping that POST
 * yet, so we surface a friendly "not yet available" toast on 404 — same
 * pattern as the revoke flow.
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

const DCR_REGISTRATION_URL = "https://mcp.smplify.com/oauth/register";
const MCP_ENDPOINT = "https://mcp.smplify.com/mcp";

const CLAUDE_SNIPPET = `{
  "mcpServers": {
    "smplify": {
      "url": "${MCP_ENDPOINT}",
      "transport": "http"
    }
  }
}`;

const CURSOR_SNIPPET = `{
  "mcpServers": {
    "smplify": {
      "url": "${MCP_ENDPOINT}"
    }
  }
}`;

const INSTALLER_SCRIPT = `curl -sSL https://mcp.smplify.com/install | bash`;

export interface RegisterMcpClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RegisterMcpClientDialog({ open, onOpenChange }: RegisterMcpClientDialogProps) {
  const [tab, setTab] = useState("dcr");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Register an MCP client</DialogTitle>
          <DialogDescription>
            Connect an AI agent or custom tool to your Smplify tenant.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="dcr">OAuth DCR</TabsTrigger>
            <TabsTrigger value="static">Static client</TabsTrigger>
            <TabsTrigger value="installer">Installer script</TabsTrigger>
          </TabsList>

          <TabsContent value="dcr" className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Recommended. Paste this MCP endpoint into your client's config — it
              will register itself over OAuth (RFC 7591) and show up here.
            </p>
            <CopyField label="MCP endpoint" value={MCP_ENDPOINT} />
            <CopyField label="Registration URL" value={DCR_REGISTRATION_URL} mono />
            <div>
              <Label className="text-xs">Claude Desktop · claude_desktop_config.json</Label>
              <CodeBlock value={CLAUDE_SNIPPET} />
            </div>
            <div>
              <Label className="text-xs">Cursor · mcp.json</Label>
              <CodeBlock value={CURSOR_SNIPPET} />
            </div>
          </TabsContent>

          <TabsContent value="static" className="space-y-4 mt-4">
            <StaticClientForm onDone={() => onOpenChange(false)} />
          </TabsContent>

          <TabsContent value="installer" className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              One-line installer auto-detects MCP-capable apps on your machine and
              wires up the right config.
            </p>
            <CodeBlock value={INSTALLER_SCRIPT} />
            <p className="text-xs text-muted-foreground">
              Stretch — the installer endpoint isn't live yet.
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function StaticClientForm({ onDone }: { onDone: () => void }) {
  const [displayName, setDisplayName] = useState("");
  const [redirectURIs, setRedirectURIs] = useState("");
  const [scopes, setScopes] = useState("mcp:read");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      toast.error("Display name is required");
      return;
    }
    setSubmitting(true);
    // Static-client registration endpoint is not yet shipping — show a
    // friendly "not yet available" message rather than firing a request
    // that would 404.
    setTimeout(() => {
      setSubmitting(false);
      toast.message("Static client registration not yet available", {
        description:
          "Use the OAuth DCR tab for now. The /admin/mcp/clients POST endpoint will ship in P2.",
      });
      onDone();
    }, 200);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="dn" className="text-xs">Display name</Label>
        <Input
          id="dn"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="My custom agent"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ru" className="text-xs">Redirect URIs (one per line)</Label>
        <Textarea
          id="ru"
          rows={3}
          value={redirectURIs}
          onChange={(e) => setRedirectURIs(e.target.value)}
          placeholder="https://my-agent.example.com/oauth/callback"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="sc" className="text-xs">Scopes (space-separated)</Label>
        <Input
          id="sc"
          value={scopes}
          onChange={(e) => setScopes(e.target.value)}
          placeholder="mcp:read mcp:edr:read"
        />
        <p className="text-[11px] text-muted-foreground">
          Use <code className="font-mono">mcp:admin</code> sparingly — it grants
          full write access across all planes.
        </p>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onDone} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Registering…" : "Register client"}
        </Button>
      </div>
    </div>
  );
}

function CopyField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2">
        <Input
          readOnly
          value={value}
          className={mono ? "font-mono text-xs" : "text-xs"}
        />
        <Button variant="outline" size="icon" onClick={copy} aria-label={`Copy ${label}`}>
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function CodeBlock({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px] text-foreground">
        {value}
      </pre>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-1.5 top-1.5 h-6 w-6"
        onClick={copy}
        aria-label="Copy"
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </Button>
    </div>
  );
}
